import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Dialog from '../common/dialog'
import { Slider, SliderProps, TextField } from '@mui/material'
import { isValidEthereumAddress, isValidTronAddress, numberWithCommas } from '../../utils'
import { textFieldWhiteBg } from '../../theme/custom-styles'
import { parseNumber } from '../customer-companies-management/utils'
import axios from 'axios'

export const TransferCryptoFundsModal = ({
  feeToPayInETH,
  setFeeToPayInETH,
  feeToPayInTRX,
  feeToPayInUSD,
  selectedNetwork,
  setFeeToPayInTRX,
  setFeeToPayInUSD,
  walletUSDTBalance,
  walletUSDCBalance,
  walletNativeBalance,
  walletAddressToSendTo,
  amountToTransferValue,
  handleOpenStateOfModal,
  setAmountToTransferValue,
  setWalletAddressToSendTo,
  handleOpenConfirmationModal
}: {
  feeToPayInETH: number
  setFeeToPayInETH: Dispatch<SetStateAction<number>>
  feeToPayInUSD: number
  feeToPayInTRX: number
  selectedNetwork: string
  walletUSDTBalance: string
  walletUSDCBalance: string
  walletNativeBalance: string
  walletAddressToSendTo: string
  amountToTransferValue: string
  setFeeToPayInUSD: Dispatch<SetStateAction<number>>
  setFeeToPayInTRX: Dispatch<SetStateAction<number>>
  handleOpenStateOfModal: (openState: boolean) => void
  setWalletAddressToSendTo: Dispatch<SetStateAction<string>>
  setAmountToTransferValue: Dispatch<SetStateAction<string>>
  handleOpenConfirmationModal: (feeToPayInTRX: number, walletAddressToSendTo: string, amountToTransferValue: string) => void
}) => {
  const { t } = useTranslation(['crypto-management'])

  const [currentTRXPrice, setCurrentTRXPrice] = useState<number>(0)
  const [currentETHPrice, setCurrentETHPrice] = useState<number>(0)
  const [actionButtonDisabled, setActionButtonDisabled] = useState(false)
  const [walletNativeBalanceInUSD, setWalletNativeBalanceInUSD] = useState<number>(0)
  const [errors, setErrors] = useState<{ setFeeToPay?: string; notEnoughFunds?: string; notEnoughFundsToPayFees?: string; amountToTransfer?: string; walletAddressToSendTo?: string } | null>(null)

  useEffect(() => {
    if (selectedNetwork === 'tron' || selectedNetwork === 'tron-paxum' || selectedNetwork === 'tron-wpagos') {
      handleCalculateCurrentTRXPrice()
    }

    if (selectedNetwork === 'eth') {
      handleCalculateCurrentETHPrice()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNetwork])

  useEffect(() => {
    if (walletNativeBalance && Number(walletNativeBalance) < 0.1 && (selectedNetwork === 'tron' || selectedNetwork === 'tron-paxum' || selectedNetwork === 'tron-wpagos')) {
      setErrors({
        notEnoughFunds: t('modal.transferCryptoFunds.errors.notEnoughFunds')
      })
      setActionButtonDisabled(true)
    }

    if (walletNativeBalance && Number(walletNativeBalance) < 0.001 && selectedNetwork === 'eth') {
      setErrors({
        notEnoughFunds: t('modal.transferCryptoFunds.errors.notEnoughFunds')
      })
      setActionButtonDisabled(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletNativeBalance])

  const handleCalculateCurrentETHPrice = async () => {
    try {
      const response = await axios.get('https://api.coingecko.com/api/v3/simple/price', {
        params: {
          ids: 'ethereum',
          vs_currencies: 'usd'
        }
      })

      console.log('ETH price:', response.data.ethereum.usd)
      setCurrentETHPrice(response.data.ethereum.usd)
      setWalletNativeBalanceInUSD(response.data.ethereum.usd * Number(walletNativeBalance))
    } catch (error) {
      console.error('Error fetching TRX price:', error)
      throw error
    }
  }

  const handleCalculateCurrentTRXPrice = async () => {
    try {
      const response = await axios.get('https://api.coingecko.com/api/v3/simple/price', {
        params: {
          ids: 'tron',
          vs_currencies: 'usd'
        }
      })

      console.log('TRX price:', response.data.tron.usd)
      setCurrentTRXPrice(response.data.tron.usd)
      setWalletNativeBalanceInUSD(response.data.tron.usd * Number(walletNativeBalance))
    } catch (error) {
      console.error('Error fetching TRX price:', error)
      throw error
    }
  }

  const handleSubmitCryprtoFundsTransfer = () => {
    setErrors(null)
    let extractedAmountToTransferValue = 0
    const errorsToSet = {} as { setFeeToPay: string; amountToTransfer: string; walletAddressToSendTo: string }

    if (!amountToTransferValue || (amountToTransferValue && Number(amountToTransferValue) === 0)) {
      errorsToSet.amountToTransfer = t('modal.transferCryptoFunds.errors.amountToSend.emptyField')
    }

    if (!walletAddressToSendTo) {
      errorsToSet.walletAddressToSendTo = t('modal.transferCryptoFunds.errors.walletAddressToSendTo.emptyField')
    }

    if (selectedNetwork === 'tron' || selectedNetwork === 'tron-paxum' || selectedNetwork === 'tron-wpagos') {
      extractedAmountToTransferValue = amountToTransferValue ? Number(amountToTransferValue.replaceAll(',', '').trim()) : 0
      if (amountToTransferValue && extractedAmountToTransferValue > Number(walletUSDTBalance)) {
        errorsToSet.amountToTransfer = t('modal.transferCryptoFunds.errors.amountToSend.exceedBalance')
      }

      if (walletAddressToSendTo && !isValidTronAddress(walletAddressToSendTo)) {
        errorsToSet.walletAddressToSendTo = t('modal.transferCryptoFunds.errors.walletAddressToSendTo.invalidAddress')
      }

      if (Number(feeToPayInTRX) === 0) {
        errorsToSet.setFeeToPay = t('modal.transferCryptoFunds.errors.setFeeToPay')
      }
    }

    if (selectedNetwork === 'eth') {
      extractedAmountToTransferValue = amountToTransferValue ? Number(amountToTransferValue.replaceAll(',', '').trim()) : 0
      console.log(extractedAmountToTransferValue, Number(walletUSDCBalance))
      if (amountToTransferValue && extractedAmountToTransferValue > Number(walletUSDCBalance)) {
        errorsToSet.amountToTransfer = t('modal.transferCryptoFunds.errors.amountToSend.exceedBalance')
      }

      if (walletAddressToSendTo && !isValidEthereumAddress(walletAddressToSendTo)) {
        errorsToSet.walletAddressToSendTo = t('modal.transferCryptoFunds.errors.walletAddressToSendTo.invalidAddress')
      }

      if (Number(feeToPayInETH) === 0) {
        errorsToSet.setFeeToPay = t('modal.transferCryptoFunds.errors.setFeeToPay')
      }
    }

    if (Object.keys(errorsToSet).length > 0) {
      setErrors(errorsToSet)
      return
    }

    handleOpenConfirmationModal(feeToPayInTRX, walletAddressToSendTo, String(extractedAmountToTransferValue))
  }

  const handleSetMaximumToSend = () => {
    const amountToTransferValueToSet =
      selectedNetwork === 'tron' || selectedNetwork === 'tron-paxum' || selectedNetwork === 'tron-wpagos'
        ? handleGetAmountToTransferValue(String(walletUSDTBalance))
        : handleGetAmountToTransferValue(String(walletUSDCBalance))
    setAmountToTransferValue(amountToTransferValueToSet)
  }

  const handleGetAmountToTransferValue = (valueToSet: string) => {
    const extractedNumberOfValueToSet = valueToSet.replaceAll(',', '').trim()

    if (isNaN(Number(extractedNumberOfValueToSet))) return ''

    if (extractedNumberOfValueToSet === '') return ''

    return parseNumber(extractedNumberOfValueToSet)
  }

  const handleChangeFeeToPay: SliderProps['onChange'] = (_, newValue) => {
    if (typeof newValue === 'number') {
      if (newValue > Number(walletNativeBalance)) {
        setErrors({
          notEnoughFundsToPayFees: t('modal.transferCryptoFunds.errors.notEnoughFundsToPayFees')
        })
        setActionButtonDisabled(true)
      } else {
        setErrors(null)
        setActionButtonDisabled(false)
      }

      if (selectedNetwork === 'tron' || selectedNetwork === 'tron-paxum' || selectedNetwork === 'tron-wpagos') {
        setFeeToPayInTRX(newValue)
        setFeeToPayInUSD(Number(newValue) * currentTRXPrice)
        return
      }

      setFeeToPayInETH(newValue)
      setFeeToPayInUSD(Number(newValue) * currentETHPrice)
    }
  }

  return (
    <Dialog
      actionButtonDisabled={actionButtonDisabled}
      dialogTitle={t('modal.transferCryptoFunds.title')}
      handleDialogAction={handleSubmitCryprtoFundsTransfer}
      actionButtonText={t('modal.transferCryptoFunds.actionButtonText')}
      handleDialogOpenState={(openState: boolean) => {
        handleOpenStateOfModal(openState)

        if (openState === false) {
          setErrors(null)
          setWalletAddressToSendTo('')
          setAmountToTransferValue('')
        }
      }}
    >
      <div
        style={{ textAlign: 'center', lineHeight: '150%' }}
        className="company-customer-funding-modal-main-instruction"
      >{`${t('modal.transferCryptoFunds.mainInstruction')}:`}</div>
      <div className="company-customer-funding-content-container">
        <div className="trade-module-container">
          <div className="operation-container">
            <div
              className="operation-title"
              style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}
            >
              <div>{t('modal.transferCryptoFunds.amountToSend')}:</div>
              <div
                onClick={handleSetMaximumToSend}
                style={{ fontWeight: '700', color: 'rgb(46, 211, 183)', cursor: 'pointer' }}
              >
                MAX
              </div>
            </div>
            <div className="operation-calculation-container">
              <div className="operation-text-field-container">
                <TextField
                  fullWidth
                  placeholder="0"
                  variant="standard"
                  sx={textFieldWhiteBg}
                  error={!!errors?.amountToTransfer}
                  helperText={errors?.amountToTransfer}
                  value={amountToTransferValue || ''}
                  onBlur={() => {
                    if (amountToTransferValue.slice(-1) === '.') {
                      setAmountToTransferValue(amountToTransferValue.slice(0, -1))
                    }
                  }}
                  onChange={(event) => {
                    const amountToTransferValueToSet = handleGetAmountToTransferValue(String(event.target.value))

                    if (amountToTransferValueToSet === '') {
                      setAmountToTransferValue(amountToTransferValueToSet)
                      return
                    }

                    setAmountToTransferValue(`${amountToTransferValueToSet}`)
                  }}
                />
              </div>
              {selectedNetwork === 'tron' || selectedNetwork === 'tron-paxum' || selectedNetwork === 'tron-wpagos' ? (
                <div className="operation-currency-data-container">USDT</div>
              ) : (
                <div className="operation-currency-data-container">USDC</div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div
        style={{ marginTop: '20px' }}
        className="company-customer-funding-content-container"
      >
        <div className="trade-module-container">
          <div className="operation-container">
            <div className="operation-title">{t('modal.transferCryptoFunds.walletAddressToSendTo')}</div>
            <div className="operation-calculation-container">
              <div
                style={{ width: '100%' }}
                className="operation-text-field-container"
              >
                <TextField
                  fullWidth
                  variant="standard"
                  value={walletAddressToSendTo || ''}
                  error={!!errors?.walletAddressToSendTo}
                  helperText={errors?.walletAddressToSendTo}
                  label={t('modal.transferCryptoFunds.placeholder')}
                  onChange={(event) => setWalletAddressToSendTo(event.target.value)}
                  sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px' } }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        style={{ marginTop: '20px' }}
        className="company-customer-funding-content-container"
      >
        <div className="trade-module-container">
          <div
            style={{ gap: '20px' }}
            className="operation-container"
          >
            <div
              className="operation-title crypto-operation-title"
              style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}
            >
              <div>{t('modal.transferCryptoFunds.feeToPay')}:</div>
              <div
                className="native-balance-text"
                style={{ fontWeight: '700', color: 'rgb(46, 211, 183)' }}
              >{`${t('modal.transferCryptoFunds.currentWalletBalance')}: ${parseNumber(Number(walletNativeBalance))} ${
                selectedNetwork === 'tron' || selectedNetwork === 'tron-paxum' || selectedNetwork === 'tron-wpagos' ? 'TRX' : 'ETH'
              } ≈ $ ${numberWithCommas(Number(walletNativeBalanceInUSD).toFixed(2))} USD`}</div>
            </div>
            <div
              className="operation-calculation-container"
              style={!errors?.notEnoughFunds ? { width: '100%', flexDirection: 'column' } : {}}
            >
              {errors?.notEnoughFunds ? (
                <div
                  className="error-message"
                  style={{ marginTop: '0px' }}
                >
                  {errors.notEnoughFunds}
                </div>
              ) : (
                <>
                  <div className="crypto-operation-fee-container">
                    <div className="operation-text-field-container">
                      <Slider
                        step={selectedNetwork === 'tron' || selectedNetwork === 'tron-paxum' || selectedNetwork === 'tron-wpagos' ? 0.1 : 0.000001}
                        defaultValue={0}
                        aria-label="Default"
                        valueLabelDisplay="auto"
                        onChange={handleChangeFeeToPay}
                        max={selectedNetwork === 'tron' || selectedNetwork === 'tron-paxum' || selectedNetwork === 'tron-wpagos' ? 1000 : 0.1}
                        sx={{
                          color: '#2ed3b7',
                          '& .MuiSlider-thumb': {
                            backgroundColor: '#107569'
                          },
                          '& .MuiSlider-track': {
                            backgroundColor: '#2ed3b7'
                          },
                          '& .MuiSlider-rail': {
                            backgroundColor: '#ccc'
                          }
                        }}
                      />
                    </div>
                    {selectedNetwork === 'tron' || selectedNetwork === 'tron-paxum' || selectedNetwork === 'tron-wpagos' ? (
                      <div className="operation-currency-data-container">{`${parseNumber(Number(feeToPayInTRX))} TRX ≈ $ ${numberWithCommas(Number(feeToPayInUSD).toFixed(2))} USD`}</div>
                    ) : (
                      <div className="operation-currency-data-container">{`${parseNumber(Number(feeToPayInETH))} ETH ≈ $ ${numberWithCommas(Number(feeToPayInUSD).toFixed(2))} USD`}</div>
                    )}
                  </div>
                  {(errors?.notEnoughFundsToPayFees || errors?.setFeeToPay) && (
                    <div
                      className="error-message"
                      style={{ marginTop: '8px' }}
                    >
                      {String(errors?.notEnoughFundsToPayFees || errors?.setFeeToPay)}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  )
}
